import {
	Button,
	Header,
	Icon,
	type JVSIcons,
	Label,
	Popup,
	Segment,
} from '@mairistem/composants';
import { isEmpty } from 'lodash';
import type React from 'react';
import IconBlockNotePresentation from '../../../../constants/simulation/iconBlockNotePresentation';
import type LigneNotePresentation from '../../../../interfaces/simulation/ligneNotePresentation';
import BlockList from './BlockList';
import './Block.css';

const typeBlocks = [
	{
		type: 'Texte rédigé',
		icon: IconBlockNotePresentation.TEXTE,
		blocks: [
			{ codeBlock: 'Texte', label: 'Texte' },
			{
				codeBlock: 'Cadre général du budget',
				label: 'Cadre général du budget',
			},
			{
				codeBlock: 'Section de fonctionnement',
				label: 'Section de fonctionnement',
			},
		],
	},
	{
		type: 'Graphique',
		icon: IconBlockNotePresentation.GRAPHIQUE,
		blocks: [{ codeBlock: 'Graphique', label: 'Graphique' }],
	},
	{
		type: 'Tableau',
		icon: IconBlockNotePresentation.TABLEAU,
		blocks: [{ codeBlock: 'Tableau', label: 'Tableau' }],
	},
	{
		type: 'Ratio',
		icon: IconBlockNotePresentation.RATIO,
		blocks: [{ codeBlock: 'Ratio', label: 'Ratio' }],
	},
	{
		type: 'Champ libre',
		icon: IconBlockNotePresentation.CHAMP_LIBRE,
		blocks: [
			{ codeBlock: 'Paragraphe', label: 'Paragraphe' },
			{ codeBlock: 'Titre', label: 'Titre' },
			{ codeBlock: 'Sous-titre', label: 'Sous-titre' },
		],
	},
];

const ICONE_PAR_CODE_BLOCK: Record<string, JVSIcons> = {
	...Object.fromEntries(
		typeBlocks.flatMap(({ blocks, icon }) =>
			blocks.map(({ codeBlock }) => [codeBlock, icon]),
		),
	),
	// Bloc historique, absent de la palette d'ajout
	Comparatif: IconBlockNotePresentation.TABLEAU,
};

interface BlockProps {
	onDelete: (ligne: LigneNotePresentation) => void;
	onAdd: (ligne: LigneNotePresentation, codeBlock: string) => void;
	onUpdate: (ligne: LigneNotePresentation) => void;
	ligne: LigneNotePresentation;
	isLastLigne: boolean;
	dragHandle?: React.HTMLAttributes<HTMLElement>;
}

const Block: React.FC<BlockProps> = ({
	onDelete,
	onUpdate,
	onAdd,
	ligne,
	isLastLigne,
	dragHandle,
}: BlockProps) => {
	const { contenuCourt, codeBlock } = ligne;

	const typeBlock =
		ICONE_PAR_CODE_BLOCK[codeBlock] ?? IconBlockNotePresentation.AUTRE;
	const isChampLibre = typeBlock === IconBlockNotePresentation.CHAMP_LIBRE;

	const handleBlockSelect = (block) => {
		onAdd(ligne, block.codeBlock);
	};

	const handleDelete = () => {
		onDelete(ligne);
	};

	const handleUpdate = () => {
		onUpdate(ligne);
	};

	return (
		<div className="block-note-presentation">
			<Segment className="block-carte">
				<span className="block-poignee" {...dragHandle}>
					<Icon iconSet="Lucide" color="grey" name="GripVertical" />
				</span>
				<Label circular className="block-type">
					<Icon iconSet="Lucide" name={typeBlock} color="white" size={18} />
				</Label>
				<Header as="h5" className="block-libelle">
					{codeBlock}
					{isChampLibre && !isEmpty(contenuCourt) && (
						<Header.Subheader>{contenuCourt}</Header.Subheader>
					)}
				</Header>
				<div className="block-menu">
					{isChampLibre && (
						<Popup
							inverted
							position="top center"
							content="Modifier le bloc"
							trigger={
								<Button
									basic
									circular
									icon
									type="button"
									aria-label="Modifier le bloc"
									data-testid="settingBlockNotePresentation"
									onClick={handleUpdate}
								>
									<Icon iconSet="Lucide" name="Settings" size={16} />
								</Button>
							}
						/>
					)}
					{!isLastLigne && (
						<Popup
							inverted
							position="top center"
							content="Supprimer le bloc"
							trigger={
								<Button
									basic
									circular
									icon
									type="button"
									aria-label="Supprimer le bloc"
									className="block-suppression"
									data-testid="deleteBlockNotePresentation"
									onClick={handleDelete}
								>
									<Icon iconSet="Lucide" name="CircleX" size={16} />
								</Button>
							}
						/>
					)}
				</div>
			</Segment>
			<div className="block-ajout">
				<BlockList
					onSelect={handleBlockSelect}
					blocks={typeBlocks}
					popupTrigger={
						<Button
							basic
							circular
							icon
							type="button"
							aria-label="Ajouter un bloc en dessous"
							data-testid="addBlockToNotePresentation"
						>
							<Icon iconSet="Lucide" name="Plus" size={16} />
						</Button>
					}
				/>
			</div>
		</div>
	);
};

export default Block;
